import { TableOutlined } from '@ant-design/icons'
import { Button, Modal } from 'antd'
import { SetFieldAccessoryValue } from 'components/form/formTypes'
import type { AllowableStressRec } from 'features/indicationTriage/cards/ffsCalc/mawpSection/allowableStress/allowableStressValues'
import { MinTensileStrengthTable } from 'features/indicationTriage/cards/ffsCalc/mawpSection/allowableStress/MetalAllowableStressTable'
import { FC, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurementUnit } from 'types'

/** Matches the sibling source-picker accessory's indent under its field. */
const Accessory = styled.div`
  padding-left: 1.5rem;
`

/**
 * Inline accessory for the FFS `yield_strength` (allowable stress, S) field:
 * opens an ASME B31.3 Table A-1 material lookup in a modal. Clicking a material
 * row × temperature cell writes that S value into the field via `setFieldValue`.
 *
 * `records` is the component-type-specific dataset resolved by the caller
 * (`FfsAssetModelEditor`). When it is null/empty — no dataset exists for this
 * component type — the picker renders nothing, so the button is hidden.
 *
 * The table is left UNCONTROLLED (no `stressValue`): the accessory holds no value
 * state and simply wires the table's `onValueClicked` (fired only on a genuine
 * user click) straight to `setFieldValue`, converting to MPa.
 */
export const MaterialStressPickerAccessory: FC<{
  setFieldValue: SetFieldAccessoryValue
  records: readonly AllowableStressRec[] | null
}> = ({ setFieldValue, records }) => {
  const [open, setOpen] = useState(false)

  if (!records || records.length === 0) return null

  return (
    <Accessory>
      <Button
        type='link'
        size='small'
        icon={<TableOutlined />}
        onClick={() => setOpen(true)}
        style={{ padding: 0 }}>
        Pick allowable stress (S)
      </Button>
      <Modal
        title='Pick an allowable stress (S) — ASME B31.3 Table A-1'
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={1000}>
        <MinTensileStrengthTable
          records={records}
          onValueClicked={(value) =>
            setFieldValue(value.convertTo(GeckoMeasurementUnit.Megapascals).num)
          }
        />
      </Modal>
    </Accessory>
  )
}
